/**
 * Hook personnalisé pour les notifications en temps réel via WebSocket
 * Gère la connexion, la reconnexion automatique et la réception des notifications
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convertir http(s):// en ws(s)://
const getWebSocketUrl = (backendUrl, userId) => {
  if (!backendUrl || !userId) return null;
  
  let wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
  // Enlever /api s'il est présent
  wsUrl = wsUrl.replace('/api', '');
  return `${wsUrl}/ws/${userId}`;
};

const useNotificationWebSocket = (enabled = true) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastNotification, setLastNotification] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 secondes

  // Callback pour les nouvelles notifications
  const onNewNotification = useCallback((notification) => {
    setLastNotification(notification);
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Garder les 50 dernières
    setUnreadCount(prev => prev + 1);
    
    // Afficher un toast pour les notifications importantes
    if (notification.priority === 'high' || notification.priority === 'critical') {
      toast.info(notification.titre, {
        description: notification.message.substring(0, 100),
        duration: 5000,
        action: {
          label: 'Voir',
          onClick: () => {
            // Navigation vers la notification si nécessaire
            window.location.href = '/membre';
          }
        }
      });
    } else {
      toast.info(notification.titre, {
        description: notification.message.substring(0, 80) + '...',
        duration: 4000
      });
    }
  }, []);

  // Mettre à jour le compteur de non lues
  const updateUnreadCount = useCallback((count) => {
    setUnreadCount(count);
  }, []);

  // Marquer une notification comme lue (local)
  const markAsReadLocal = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, lu: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Marquer toutes comme lues (local)
  const markAllAsReadLocal = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, lu: true })));
    setUnreadCount(0);
  }, []);

  // Fonction de connexion
  const connect = useCallback(() => {
    if (!enabled || !user?.id) return;
    
    const wsUrl = getWebSocketUrl(BACKEND_URL, user.id);
    if (!wsUrl) return;
    
    // Ne pas reconnecter si déjà connecté
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        
        // Démarrer le ping heartbeat
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping toutes les 30 secondes
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'connection_established':
              break;
              
            case 'notification':
              // Nouvelle notification reçue
              if (data.notification) {
                onNewNotification(data.notification);
              }
              break;
              
            case 'notification_update':
              // Mise à jour d'une notification
              if (data.data?.action === 'read') {
                markAsReadLocal(data.data.notification_id);
              } else if (data.data?.action === 'all_read') {
                markAllAsReadLocal();
              }
              break;
              
            case 'unread_count_update':
              // Mise à jour du compteur
              if (typeof data.data?.unread_count === 'number') {
                updateUnreadCount(data.data.unread_count);
              }
              break;
              
            case 'pong':
              // Réponse au ping, connexion vivante
              break;
              
            default:
          }
        } catch {
        }
      };
      
      ws.onerror = (_error) => {
        setIsConnected(false);
      };
      
      ws.onclose = (event) => {
        setIsConnected(false);
        
        // Arrêter le ping
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Tenter une reconnexion si ce n'était pas une fermeture volontaire
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay * reconnectAttemptsRef.current);
        }
      };
      
      wsRef.current = ws;
      
    } catch {
    }
  }, [enabled, user?.id, onNewNotification, markAsReadLocal, markAllAsReadLocal, updateUnreadCount]);

  // Fonction de déconnexion
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Fermeture volontaire');
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  // Effet pour gérer la connexion/déconnexion
  useEffect(() => {
    if (enabled && user?.id) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [enabled, user?.id, connect, disconnect]);

  // Effet pour reconnecter si la page devient visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && enabled && user?.id) {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          connect();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, user?.id, connect]);

  return {
    isConnected,
    notifications,
    unreadCount,
    lastNotification,
    markAsReadLocal,
    markAllAsReadLocal,
    updateUnreadCount,
    reconnect: connect,
    disconnect
  };
};

export default useNotificationWebSocket;
